// Search, ranking and grouping for the location picker (GroupPicker.vue). Pure
// functions so they stay fast and testable at 600+ locations.
import type { GroupSummary } from '@/util/activeGroup'

export const SEARCH_THRESHOLD = 9
export const GROUPING_THRESHOLD = 50

export function normalize(s: any): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

// Every query word must match somewhere. Name starting with the query ranks first,
// then name containing every word, then a match in any other field.
export function rankGroups(list: GroupSummary[], query: string): GroupSummary[] {
  const tokens = normalize(query).split(/\s+/).filter(Boolean)
  if (!tokens.length) return list.slice()
  const scored: { g: GroupSummary; score: number }[] = []
  for (const g of list) {
    const name = normalize(g.name)
    const other = [g.city, g.state, g.country, g.code, g.company_number].map(normalize).join(' ')
    const hay = name + ' ' + other
    if (!tokens.every(t => hay.includes(t))) continue
    let score = 2
    if (tokens.every(t => name.includes(t))) score = 1
    if (name.startsWith(tokens[0])) score = 0
    scored.push({ g, score })
  }
  scored.sort((a, b) => a.score - b.score || a.g.position - b.g.position || a.g.id - b.g.id)
  return scored.map(s => s.g)
}

export interface GroupSection {
  header: string | null
  items: GroupSummary[]
}

// Below the threshold: one flat list. At or above it: one header per state. When the
// list spans more than one country the header also names the country ("TX, US"), and a
// location with no state falls under its country (under Other when there is one country). A few locations abroad must never
// collapse hundreds of home locations into one country header.
export function sectionGroups(list: GroupSummary[]): GroupSection[] {
  if (list.length < GROUPING_THRESHOLD) return [{ header: null, items: list }]
  const countries = new Set(list.map(g => normalize(g.country)))
  const multiCountry = countries.size > 1
  const keyOf = (g: GroupSummary) => {
    if (g.state) return multiCountry && g.country ? g.state + ', ' + g.country : g.state
    return multiCountry && g.country ? g.country : 'Other'
  }
  const map = new Map<string, GroupSummary[]>()
  for (const g of list) {
    const k = keyOf(g)
    if (!map.has(k)) map.set(k, [])
    map.get(k)!.push(g)
  }
  return Array.from(map.keys())
    .sort((a, b) => a.localeCompare(b))
    .map(k => ({
      header: k,
      items: map.get(k)!.slice().sort((a, b) => a.position - b.position || a.id - b.id),
    }))
}

export function highlightRanges(text: string, query: string): { text: string; hit: boolean }[] {
  const t = normalize(query).split(/\s+/).filter(Boolean)
  if (!t.length || !text) return [{ text: text || '', hit: false }]
  // Marks per character on the lowercased original; accent-stripped words may miss
  // accented letters in the highlight, never in the filter.
  const lower = text.toLowerCase()
  const marks = new Array(text.length).fill(false)
  for (const tok of t) {
    let i = lower.indexOf(tok)
    while (i >= 0) {
      for (let k = i; k < i + tok.length; k++) marks[k] = true
      i = lower.indexOf(tok, i + 1)
    }
  }
  const out: { text: string; hit: boolean }[] = []
  for (let i = 0; i < text.length; i++) {
    const last = out[out.length - 1]
    if (last && last.hit === marks[i]) last.text += text[i]
    else out.push({ text: text[i], hit: marks[i] })
  }
  return out
}

export function locationLine(g: { city?: string | null; state?: string | null } | null | undefined): string {
  if (!g) return ''
  return [g.city, g.state].filter(Boolean).join(', ')
}
